import { useEffect, useMemo, useState } from "react"
import {
  AlertCircle,
  Check,
  ExternalLink,
  Loader2,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import {
  presignAgentConversationAttachment,
  type AgentConversationAttachmentPresignResponse,
} from "./api/agent-conversation-client"
import type { RobinChoicesAttachment, RobinFileAttachment } from "./robin-types"
import {
  attachmentExtensionLabel,
  documentKindLabel,
  formatBytes,
  isImageAttachment,
  isPdfAttachment,
  isPreviewableAttachment,
  miniatureLineWidths,
  pdfThumbnailUrl,
  previewErrorMessage,
} from "./utils/attachment-preview"

const CARD_CLASS =
  "mt-3 w-full max-w-md overflow-hidden rounded-xl border bg-card text-card-foreground shadow-sm"

/** Historical ask_user choices stay visible only after they can no longer answer. */
export function ChoicesCard({
  attachment,
  onChoose,
}: {
  attachment: RobinChoicesAttachment
  onChoose: (choiceId: string) => void
}) {
  const { options, readOnly, resolved } = attachment
  if (options.length === 0) return null

  const disabled = readOnly || Boolean(resolved)
  return (
    <div className={cn(CARD_CLASS, "divide-y")}>
      {options.map((option) => {
        const isChosen = resolved === option.id
        return (
          <button
            key={option.id}
            type="button"
            disabled={disabled}
            onClick={() => onChoose(option.id)}
            className={cn(
              "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors",
              !disabled && "hover:bg-foreground/[0.04]",
              resolved && !isChosen && "opacity-45",
              readOnly && !resolved && "cursor-default",
            )}
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-brand-ink">
              {initials(option.name)}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">{option.name}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {option.meta}
              </span>
            </span>
            {isChosen && <Check className="h-4 w-4 shrink-0 text-brand-ink" />}
          </button>
        )
      })}
    </div>
  )
}

type PreviewState =
  | { status: "idle" }
  | { status: "loading" }
  | {
      status: "ready"
      url: string
      data?: AgentConversationAttachmentPresignResponse
    }
  | { status: "error"; message: string }

type ThumbnailState =
  | { status: "loading" }
  | { status: "ready"; data: AgentConversationAttachmentPresignResponse }
  | { status: "error" }

export function FileAttachmentsList({
  conversationId,
  attachments,
  align = "start",
}: {
  conversationId: string
  attachments?: RobinFileAttachment[]
  align?: "start" | "end"
}) {
  const visibleAttachments = useMemo(
    () => attachments?.filter((attachment) => attachment.id && attachment.fileName) ?? [],
    [attachments],
  )
  const visualThumbnailAttachments = useMemo(
    () =>
      visibleAttachments.filter(
        (attachment) =>
          isPreviewableAttachment(attachment) && !attachment.localPreviewUrl,
      ),
    [visibleAttachments],
  )
  const [openAttachment, setOpenAttachment] = useState<RobinFileAttachment | null>(null)
  const [preview, setPreview] = useState<PreviewState>({ status: "idle" })
  const [thumbnails, setThumbnails] = useState<Record<string, ThumbnailState>>({})
  const [reloadKey, setReloadKey] = useState(0)
  const previewUrl = preview.status === "ready" ? preview.url : null
  const visualThumbnailKey = visualThumbnailAttachments
    .map((attachment) => attachment.id)
    .join("|")

  useEffect(() => {
    if (visualThumbnailAttachments.length === 0) return

    const controller = new AbortController()
    setThumbnails((current) => {
      const next = { ...current }
      for (const attachment of visualThumbnailAttachments) {
        if (!next[attachment.id]) next[attachment.id] = { status: "loading" }
      }
      return next
    })

    void Promise.all(
      visualThumbnailAttachments.map(async (attachment) => {
        try {
          const data = await presignAgentConversationAttachment(
            conversationId,
            attachment.id,
            controller.signal,
          )
          return [attachment.id, { status: "ready" as const, data }] as const
        } catch {
          return [attachment.id, { status: "error" as const }] as const
        }
      }),
    ).then((entries) => {
      if (controller.signal.aborted) return
      setThumbnails((current) => ({ ...current, ...Object.fromEntries(entries) }))
    })

    return () => controller.abort()
  }, [conversationId, visualThumbnailKey, visualThumbnailAttachments])

  useEffect(() => {
    if (!openAttachment) {
      setPreview({ status: "idle" })
      return
    }

    const controller = new AbortController()
    setPreview({ status: "loading" })

    if (openAttachment.localPreviewUrl) {
      setPreview({ status: "ready", url: openAttachment.localPreviewUrl })
      return () => controller.abort()
    }

    void presignAgentConversationAttachment(
      conversationId,
      openAttachment.id,
      controller.signal,
    )
      .then((data) => {
        setPreview({ status: "ready", url: data.url, data })
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        setPreview({ status: "error", message: previewErrorMessage(error) })
      })

    return () => controller.abort()
  }, [conversationId, openAttachment, reloadKey])

  if (visibleAttachments.length === 0) return null

  return (
    <>
      <div
        className={cn(
          "mt-2 flex max-w-full flex-wrap gap-1.5",
          align === "end" && "justify-end",
        )}
      >
        {visibleAttachments.map((attachment) => (
          <AttachmentThumbnail
            key={attachment.id}
            attachment={attachment}
            thumbnail={thumbnails[attachment.id]}
            onOpen={() => {
              setOpenAttachment(attachment)
              setReloadKey(0)
            }}
          />
        ))}
      </div>

      <Dialog
        open={Boolean(openAttachment)}
        onOpenChange={(open) => {
          if (!open) setOpenAttachment(null)
        }}
      >
        {openAttachment && (
          <DialogContent className="flex h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-none items-center justify-center border-0 bg-transparent p-0 shadow-none sm:rounded-none [&>button:last-child]:rounded-full [&>button:last-child]:bg-background/90 [&>button:last-child]:p-2 [&>button:last-child]:opacity-100 [&>button:last-child]:shadow-sm">
            <DialogTitle className="sr-only">{openAttachment.fileName}</DialogTitle>
            <DialogDescription className="sr-only">
              {documentKindLabel(openAttachment.documentKind)} attachment,{" "}
              {formatBytes(openAttachment.sizeBytes)}
            </DialogDescription>
            <AttachmentPreviewBody
              attachment={openAttachment}
              preview={preview}
              onRetry={() => setReloadKey((key) => key + 1)}
            />
            {previewUrl && (
              <a
                href={previewUrl}
                target="_blank"
                rel="noreferrer"
                className="absolute bottom-3 right-3 inline-flex h-9 items-center gap-2 rounded-full bg-background/90 px-3 text-xs font-medium text-foreground shadow-sm ring-1 ring-border/70 transition-colors hover:bg-background"
              >
                Open file
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </DialogContent>
        )}
      </Dialog>
    </>
  )
}

function AttachmentThumbnail({
  attachment,
  thumbnail,
  onOpen,
}: {
  attachment: RobinFileAttachment
  thumbnail?: ThumbnailState
  onOpen: () => void
}) {
  const thumbnailUrl =
    attachment.localPreviewUrl ??
    (thumbnail?.status === "ready" ? thumbnail.data.url : null)
  const label = `${documentKindLabel(attachment.documentKind)}, ${formatBytes(
    attachment.sizeBytes,
  )}`

  return (
    <button
      type="button"
      onClick={onOpen}
      title={`${attachment.fileName} - ${label}`}
      aria-label={`Preview ${attachment.fileName}`}
      className="group relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-border/70 bg-background shadow-sm transition focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 hover:brightness-95"
    >
      {isImageAttachment(attachment) ? (
        <ImageThumbnailSurface url={thumbnailUrl} />
      ) : isPdfAttachment(attachment) ? (
        <PdfThumbnailSurface url={thumbnailUrl} />
      ) : (
        <DocumentThumbnailSurface
          label={attachmentExtensionLabel(attachment)}
          text={attachment.summary ?? attachment.extractedTextPreview}
        />
      )}
      {!isImageAttachment(attachment) && (
        <span className="absolute bottom-1 right-1 rounded bg-background/90 px-1 text-[10px] font-semibold leading-4 text-foreground shadow-sm">
          {attachmentExtensionLabel(attachment)}
        </span>
      )}
    </button>
  )
}

function ImageThumbnailSurface({ url }: { url: string | null }) {
  if (url) {
    return (
      <img
        src={url}
        alt=""
        className="h-full w-full object-cover"
        draggable={false}
      />
    )
  }

  return (
    <span className="relative block h-full w-full overflow-hidden bg-muted">
      <span className="absolute inset-2 rounded-sm bg-background/75" />
      <span className="absolute bottom-3 left-2 h-5 w-8 -skew-x-12 rounded-sm bg-muted-foreground/20" />
      <span className="absolute right-3 top-3 h-2 w-2 rounded-full bg-muted-foreground/20" />
    </span>
  )
}

function PdfThumbnailSurface({ url }: { url: string | null }) {
  if (url) {
    return (
      <span className="block h-full w-full overflow-hidden bg-white">
        <iframe
          title=""
          src={pdfThumbnailUrl(url)}
          tabIndex={-1}
          className="pointer-events-none h-[220%] w-[220%] origin-top-left scale-[0.46] border-0 bg-white"
        />
      </span>
    )
  }

  return <DocumentThumbnailSurface label="PDF" text="" />
}

function DocumentThumbnailSurface({
  label,
  text,
}: {
  label: string
  text: string | null | undefined
}) {
  const widths = miniatureLineWidths(text || label)

  return (
    <span className="flex h-full w-full flex-col bg-background p-2 text-left">
      <span className="mb-1.5 h-1.5 w-7 rounded-full bg-foreground/25" />
      {widths.map((width, index) => (
        <span
          key={`${width}-${index}`}
          className="mb-1 h-1 rounded-full bg-muted-foreground/20"
          style={{ width: `${width}%` }}
        />
      ))}
    </span>
  )
}

function AttachmentPreviewBody({
  attachment,
  preview,
  onRetry,
}: {
  attachment: RobinFileAttachment
  preview: PreviewState
  onRetry: () => void
}) {
  if (!isPreviewableAttachment(attachment)) {
    return <AttachmentTextPreview attachment={attachment} />
  }

  if (preview.status === "idle" || preview.status === "loading") {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-background" />
      </div>
    )
  }

  if (preview.status === "error") {
    return (
      <div className="flex h-full w-full items-center justify-center p-6 text-center">
        <div className="max-w-sm rounded-lg bg-background p-5 shadow-xl">
          <AlertCircle className="mx-auto h-7 w-7 text-destructive" />
          <p className="mt-3 text-sm font-medium">{preview.message}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-4"
            onClick={onRetry}
          >
            Retry
          </Button>
        </div>
      </div>
    )
  }

  if (isImageAttachment(attachment)) {
    return (
      <div className="flex h-full w-full items-center justify-center p-4">
        <img
          src={preview.url}
          alt={attachment.fileName}
          className="max-h-full max-w-full rounded-lg object-contain shadow-2xl"
        />
      </div>
    )
  }

  if (isPdfAttachment(attachment)) {
    return (
      <iframe
        title={attachment.fileName}
        src={preview.url}
        className="h-[min(86vh,900px)] w-[min(92vw,960px)] rounded-lg border-0 bg-background shadow-2xl"
      />
    )
  }

  return <AttachmentTextPreview attachment={attachment} />
}

function AttachmentTextPreview({
  attachment,
}: {
  attachment: RobinFileAttachment
}) {
  const summary = attachment.summary?.trim()
  const extractedText = attachment.extractedTextPreview.trim()

  return (
    <div className="max-h-[86vh] w-[min(92vw,720px)] overflow-y-auto rounded-lg bg-background p-5 shadow-2xl">
      <div className="mx-auto max-w-2xl space-y-4 text-sm leading-relaxed">
        {summary && (
          <section>
            <h3 className="text-xs font-semibold uppercase text-muted-foreground">
              Summary
            </h3>
            <p className="mt-1 whitespace-pre-wrap">{summary}</p>
          </section>
        )}
        {extractedText ? (
          <section>
            <h3 className="text-xs font-semibold uppercase text-muted-foreground">
              Extracted text
            </h3>
            <p className="mt-1 whitespace-pre-wrap text-muted-foreground">
              {extractedText}
            </p>
          </section>
        ) : (
          <p className="text-muted-foreground">
            No extracted text preview is available for this file.
          </p>
        )}
      </div>
    </div>
  )
}

function initials(name: string): string {
  const clean = name.trim()
  if (!clean) return "?"
  return clean
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("")
}
